import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { Link } from 'react-router-dom';
import UpdatesSignup from '../components/UpdatesSignup';
import Footer from '../components/Footer';
import AppStoreBadges from '../components/AppStoreBadges';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const AnimatedSection: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1, rootMargin: '0px' });
  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return (
    <div
      ref={ref}
      style={
        reducedMotion
          ? undefined
          : {
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: `opacity 1.2s ease-out ${delay}ms, transform 1s ease-out ${delay}ms`
            }
      }
    >
      {children}
    </div>
  );
};

const WhyRidePulse: React.FC = () => {
  const { theme, dsColors, colors } = useTheme();

  const heroPrimary = colors.textPrimary;
  const heroSecondary = colors.textSecondary;
  const cardBorder = theme === 'light' ? 'rgba(0,0,0,0.1)' : colors.border;
  const bandBg = theme === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.02)';

  const sectionTitle = 'text-3xl sm:text-4xl font-bold mb-4';
  const sectionLead = 'text-lg sm:text-xl max-w-2xl leading-relaxed';

  const missedConnections = [
    '"Who\'s riding?" posts buried in Facebook groups',
    'Stale ride invites from days ago still showing up',
    'Group chats cluttered with old plans and confusion',
    "No clear details: when? where? what pace? who's going?",
    "Missing out on rides because you didn't see the post"
  ];

  const builtForRiders = [
    'Events with clear details, not vague posts',
    'Events archive when the ride ends - no stale content',
    'Private event chats for actual coordination',
    "See who's going before you commit",
    'Find rides you actually want to join'
  ];

  const otherAppsFocus = ['GPS navigation and route planning', 'Trip statistics and tracking', 'Scenic route discovery', 'Crash detection and safety alerts'];
  const otherAppsMiss = ['Finding riders who match your style', 'Coordinating group rides easily', 'Building a riding community'];
  const ridePulseFocus = ['Connecting riders with similar styles', 'Building riding communities', 'Coordinating group rides effortlessly', 'Private event communication'];

  const coreStrengths = [
    { title: 'Built for Group Riding', description: 'Not a generic social app or solo tracker - designed specifically for coordinating motorcycle group rides' },
    { title: 'End-to-End Solution', description: 'From finding riders to post-ride achievements - everything you need in one app' },
    { title: 'Privacy-First Design', description: 'Control exactly what you share, with whom, and when - your data stays yours' },
    { title: 'Community-Driven', description: 'Built by riders, for riders - we understand what makes group rides work' }
  ];

  const keyDifferentiators = [
    { category: 'Event Coordination', features: ['Guided event creation', 'Built-in event chat for coordination', 'Multiple check-in methods (QR, GPS, Manual)', 'Per-event privacy controls'] },
    { category: 'Safety & Sharing', features: ['GroupPulse live location sharing (host-enabled, per-rider opt-in)', 'SOS alerts with location', 'Emergency contact sharing (opt-in)', 'Adaptive battery optimization (iOS)', 'Ride location data auto-deleted after 30 days'] },
    { category: 'Social Features', features: ['Friends system with mutual connections', 'Public profiles with garage showcase', 'Achievement and badge system', 'Event discovery by location and type', 'Profile privacy presets and per-event choices'] },
    { category: 'Host Tools', features: ['Participant management dashboard', 'Chat moderation (ban & kick)', 'Host event analytics (Pro)', 'Route waypoints (Pro on iOS)', 'Weather forecasts for events'] }
  ];

  const whatWeAreNot = ['Generic social network', 'Solo-focused GPS tracker', 'Route planning only', 'Crash detection service'];
  const whatWeAre = ['Group ride coordination platform', 'Complete event management system', 'Safety-focused community app', 'Rider-to-rider connection tool'];

  const fbExamples = [
    { src: '/assets/images/IMG_1937.png', caption: 'Cancelled and still planning?' },
    { src: '/assets/images/IMG_1866.png', caption: 'Where are we going?' },
    { src: '/assets/images/IMG_1992.png', caption: 'Where is the meetup?' }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* 1 · Quiet typographic hero — the founder's voice, no photo */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-[1.08]" style={{ color: heroPrimary, textWrap: 'balance' }}>
            Why RidePulse?
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>
            I got tired of "Who's riding?" getting lost in Facebook groups. So I built this.
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
            <AppStoreBadges size="large" showApple showAndroid />
          </div>
        </div>
      </section>

      {/* 2 · The Problem — problem/solution cards + founder quote + real FB evidence */}
      <section className="py-16 md:py-24" style={{ backgroundColor: bandBg }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>The Problem That Started It All</h2>
              <p className={`${sectionLead} mx-auto`} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                "Who's riding?" should be a thing of the past.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              <div className="p-8 rounded-xl" style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}>
                <h3 className="text-2xl font-bold mb-6" style={{ color: heroPrimary }}>Missed Connections</h3>
                <ul className="space-y-3">
                  {missedConnections.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="mt-1 flex-shrink-0" style={{ color: heroSecondary }} aria-hidden="true">✗</span>
                      <span className="text-sm sm:text-base" style={{ color: heroSecondary }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-8 rounded-xl" style={{ backgroundColor: colors.surface, border: `2px solid ${dsColors.primary}55` }}>
                <h3 className="text-2xl font-bold mb-6" style={{ color: heroPrimary }}>Built for Riders</h3>
                <ul className="space-y-3">
                  {builtForRiders.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="mt-1 flex-shrink-0" style={{ color: dsColors.primary }} aria-hidden="true">✓</span>
                      <span className="text-sm sm:text-base" style={{ color: heroSecondary }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <blockquote className="max-w-3xl mx-auto text-center mb-14">
              <p className="text-lg sm:text-xl font-semibold leading-relaxed" style={{ color: heroPrimary, textWrap: 'pretty' }}>
                I'm a solo developer and rider. I lived this frustration every weekend. So I fixed it.
              </p>
            </blockquote>
            <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: heroPrimary, textWrap: 'balance' }}>
              What You Won't See Anymore
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {fbExamples.map((img) => (
                <figure key={img.src} className="text-center">
                  <div className="rounded-lg overflow-hidden mb-3" style={{ border: `1px solid ${cardBorder}` }}>
                    <img src={img.src} alt={img.caption} className="w-full h-auto" loading="lazy" />
                  </div>
                  <figcaption className="text-sm" style={{ color: heroSecondary }}>{img.caption}</figcaption>
                </figure>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 3 · The Gap in the Market — quiet two-column comparison */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>The Gap in the Market</h2>
              <p className={`${sectionLead} mx-auto`} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                Other apps focus on GPS and routes. We focus on connecting riders.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-8 rounded-xl" style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}>
                <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: heroPrimary }}>Other Motorcycle Apps</h3>
                <h4 className="text-sm font-bold mb-3 uppercase tracking-wider" style={{ color: heroSecondary }}>What They Focus On:</h4>
                <ul className="space-y-2 mb-6">
                  {otherAppsFocus.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-sm flex-shrink-0 mt-0.5" style={{ color: heroSecondary }} aria-hidden="true">•</span>
                      <span className="text-sm" style={{ color: heroSecondary }}>{item}</span>
                    </li>
                  ))}
                </ul>
                <h4 className="text-sm font-bold mb-3 uppercase tracking-wider" style={{ color: heroSecondary }}>What They Miss:</h4>
                <ul className="space-y-2">
                  {otherAppsMiss.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-sm flex-shrink-0 mt-0.5" style={{ color: heroSecondary }} aria-hidden="true">✗</span>
                      <span className="text-sm" style={{ color: heroSecondary }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-8 rounded-xl" style={{ backgroundColor: colors.surface, border: `2px solid ${dsColors.primary}55` }}>
                <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: heroPrimary }}>RidePulse</h3>
                <h4 className="text-sm font-bold mb-3 uppercase tracking-wider" style={{ color: dsColors.treadGray }}>Our Focus:</h4>
                <ul className="space-y-2 mb-6">
                  {ridePulseFocus.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-sm flex-shrink-0 mt-0.5" style={{ color: dsColors.primary }} aria-hidden="true">✓</span>
                      <span className="text-sm font-semibold" style={{ color: heroPrimary }}>{item}</span>
                    </li>
                  ))}
                </ul>
                <h4 className="text-sm font-bold mb-3 uppercase tracking-wider" style={{ color: dsColors.treadGray }}>The Goal:</h4>
                <p className="text-lg font-semibold" style={{ color: heroPrimary, textWrap: 'pretty' }}>
                  Help riders find people they actually enjoy riding with.
                </p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 4 · Core Strengths — quiet ticks */}
      <section className="py-16 md:py-24" style={{ backgroundColor: bandBg }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Our Core Strengths</h2>
              <p className={`${sectionLead} mx-auto`} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                What makes RidePulse different from every other app.
              </p>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8 max-w-3xl mx-auto">
              {coreStrengths.map((s) => (
                <li key={s.title} className="flex items-start gap-3">
                  <span className="mt-1 text-base font-extrabold flex-shrink-0" style={{ color: dsColors.primary }} aria-hidden="true">✓</span>
                  <div>
                    <h3 className="font-semibold text-base mb-0.5" style={{ color: heroPrimary }}>{s.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>{s.description}</p>
                  </div>
                </li>
              ))}
            </ul>
          </AnimatedSection>
        </div>
      </section>

      {/* 5 · Clear Focus — are/aren't */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Clear Focus</h2>
              <p className={`${sectionLead} mx-auto`} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                We know what we are - and what we're not.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              <div className="p-8 rounded-xl" style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}>
                <h3 className="text-xl font-bold mb-6 text-center" style={{ color: heroPrimary }}>What We're Not</h3>
                <ul className="space-y-4">
                  {whatWeAreNot.map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <span className="text-base flex-shrink-0" style={{ color: heroSecondary }} aria-hidden="true">✗</span>
                      <span className="text-sm sm:text-base" style={{ color: heroSecondary }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-8 rounded-xl" style={{ backgroundColor: colors.surface, border: `2px solid ${dsColors.primary}55` }}>
                <h3 className="text-xl font-bold mb-6 text-center" style={{ color: heroPrimary }}>What We Are</h3>
                <ul className="space-y-4">
                  {whatWeAre.map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <span className="text-base flex-shrink-0" style={{ color: dsColors.primary }} aria-hidden="true">✓</span>
                      <span className="text-sm sm:text-base font-semibold" style={{ color: heroPrimary }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 6 · Complete Feature Set — quiet category ticks */}
      <section className="py-16 md:py-24" style={{ backgroundColor: bandBg }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Complete Feature Set</h2>
              <p className={`${sectionLead} mx-auto`} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                Everything you need for group riding in one place.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {keyDifferentiators.map((diff) => (
                <div key={diff.category} className="p-6 rounded-xl" style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}>
                  <h3 className="text-lg font-bold mb-4" style={{ color: heroPrimary }}>{diff.category}</h3>
                  <ul className="space-y-3">
                    {diff.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <span className="text-sm mt-0.5 flex-shrink-0" style={{ color: dsColors.primary }} aria-hidden="true">✓</span>
                        <span className="text-sm break-words min-w-0" style={{ color: heroSecondary }}>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 7 · CTA */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl text-center">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ color: heroPrimary, textWrap: 'balance' }}>
              Ready to Ride Better?
            </h2>
            <p className="text-lg sm:text-xl mb-8 max-w-2xl mx-auto" style={{ color: heroSecondary, textWrap: 'pretty' }}>
              Join the riders who've had enough of the group-chat scramble.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <AppStoreBadges size="large" showApple showAndroid />
              <Link
                to="/getting-started"
                className="px-8 py-4 rounded-full font-bold text-lg motion-safe:transition-transform motion-safe:hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-0"
                style={{
                  backgroundColor: `${dsColors.primary}1f`,
                  color: dsColors.primary,
                  border: `2px solid ${dsColors.primary}`,
                  '--tw-ring-color': dsColors.primary
                } as React.CSSProperties}
              >
                See How It Works
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 8 · Sign Up for Updates */}
      <section className="py-16 md:py-24" style={{ backgroundColor: bandBg }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-2xl">
          <UpdatesSignup source="why-ridepulse" variant="inline" />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WhyRidePulse;
